import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";

const STAGES = [
  {
    n: "01",
    name: "DISCOVER",
    tagline: "Understand before you invest.",
    items: [
      "Stakeholder interviews",
      "Current-state workflows",
      "Pain points and root causes",
      "User needs",
      "System constraints",
    ],
  },
  {
    n: "02",
    name: "DEFINE",
    tagline: "Specify exactly what must change.",
    items: [
      "Business requirements",
      "Functional requirements",
      "Epics and user stories",
      "Acceptance criteria",
      "Priorities",
      "Future-state workflows",
    ],
  },
  {
    n: "03",
    name: "VALIDATE",
    tagline: "Make it tangible before it gets expensive.",
    items: [
      "Prototypes",
      "Proof-of-concepts",
      "Process simulations",
      "Solution options",
      "Vendor evaluation",
    ],
  },
  {
    n: "04",
    name: "DELIVER",
    tagline: "Keep implementation connected to intent.",
    items: [
      "Product ownership",
      "Backlog leadership",
      "Implementation oversight",
      "Dependency and risk management",
      "Vendor coordination",
      "Executive reporting",
    ],
  },
  {
    n: "05",
    name: "MEASURE",
    tagline: "Prove it achieved what was intended.",
    items: [
      "Adoption",
      "Operating outcomes",
      "Error reduction",
      "Cycle time",
      "Quality and cost",
    ],
  },
];

const TOOLS = [
  "Some problems need AI.",
  "Some need analytics.",
  "Some need automation.",
  "Some need rapid prototyping.",
  "Some need a workflow change.",
  "Some need better requirements.",
  "Some need training.",
];

const PRINCIPLES = [
  {
    n: "01",
    title: "Evidence before intervention.",
    desc: "We start from how the current system actually behaves — not from assumptions, aspirations, or vendor roadmaps.",
  },
  {
    n: "02",
    title: "The intervention matches the cause.",
    desc: "Tools are selected after diagnosis, never before. A prediction problem gets a model; an ambiguity problem gets a prototype.",
  },
  {
    n: "03",
    title: "Outcomes are measured, not assumed.",
    desc: "Every effort carries a baseline and a test. If it does not move the outcome, it is not finished.",
  },
  {
    n: "04",
    title: "The smallest sufficient change.",
    desc: "We prefer precise interventions over large programs. Most causes, once found, need less than expected.",
  },
];

export default function ApproachPage() {
  return (
    <>
      <Seo
        title="Our Approach — Improve Systems"
        siteName="Improve Systems"
        description="Discover, Define, Validate, Deliver, Measure — a service model for modernization that solves the right problems and delivers measurable outcomes."
      />
      <PageHero
        eyebrow="OUR APPROACH"
        title="Understand what needs to change before you invest in changing it."
        lede="Improve Systems is a product and transformation advisory. AI, prototyping, analytics, and automation are tools we bring in when they help — they are not our identity. Our job is to keep modernization efforts aimed at the right problems and accountable to measurable outcomes."
        testid="approach-hero"
      />

      <section data-testid="service-model-detail" className="border-b border-line">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <Reveal>
            <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
              01 — THE SERVICE MODEL
            </p>
            <h2 className="max-w-2xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
              Five stages. One clean line from first question to proven outcome.
            </h2>
          </Reveal>
          <div className="mt-14 border-t border-line">
            {STAGES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05}>
                <div
                  data-testid={`stage-${s.name.toLowerCase()}`}
                  className="grid gap-6 border-b border-line py-10 lg:grid-cols-12"
                >
                  <div className="flex items-baseline gap-5 lg:col-span-4">
                    <span className="font-mono text-[11px] text-slate-400">{s.n}</span>
                    <div>
                      <h3 className="font-mono text-sm tracking-[0.25em] text-ink">{s.name}</h3>
                      <p className="mt-2 font-serif text-xl italic text-slate-700">
                        {s.tagline}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap content-start gap-2.5 lg:col-span-8">
                    {s.items.map((item) => (
                      <span
                        key={item}
                        className="border border-line bg-white px-3.5 py-2 text-[13px] text-slate-600"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 grid gap-8 border border-line bg-white p-8 sm:p-12 lg:grid-cols-2 lg:items-center">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-slate-400">
                  THE DIFFERENTIATOR
                </p>
                <p className="mt-4 font-serif text-3xl leading-snug text-ink sm:text-4xl">
                  “Is this what you mean?”
                </p>
              </div>
              <p className="max-w-xl leading-relaxed text-slate-600">
                Our ability to prototype is a differentiator. We don't only write
                requirements documents — we quickly turn an ambiguous idea into something
                stakeholders can see and react to. That single question, asked early,
                dramatically improves discovery and requirements quality before an expensive
                implementation team starts building.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="tools-section" className="border-b border-line bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
                  02 — TOOLS, NOT IDENTITY
                </p>
                <h2 className="font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
                  The right solution depends on the cause.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <div className="border-t border-line">
                {TOOLS.map((line, i) => (
                  <Reveal key={line} delay={i * 0.05}>
                    <div className="flex items-baseline gap-6 border-b border-line py-5">
                      <span className="font-mono text-[11px] text-slate-400">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="font-serif text-2xl leading-snug text-ink sm:text-3xl">
                        {line}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <p className="mt-8 max-w-xl leading-relaxed text-slate-600">
                  We are not an AI consultancy and not a software development shop. We
                  diagnose first, let the cause dictate the tool, and stay accountable for
                  whether the outcome actually changed.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="principles-section" className="border-b border-line">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <Reveal>
            <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
              03 — OPERATING PRINCIPLES
            </p>
            <h2 className="max-w-xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
              How we work.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col bg-canvas p-7">
                  <span className="font-mono text-[11px] text-slate-400">{p.n}</span>
                  <h3 className="mt-5 font-serif text-xl leading-snug text-ink">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
